import { AdmissionResultRowDTO } from '@shared/clientTypes/DTOs/AdminMetricsDTO';
import {
  AdmissionTier,
  EU_COUNTRIES,
  NETHERLANDS_COUNTRY,
  TOP_100_RANK_LIMIT,
} from '@shared/constants';
import { GoalStatusTypes } from '@shared/enums';
import { stringify } from 'csv-stringify/sync';
import {
  availableYears,
  cycleYearOf,
  CycleYear,
  isOffer,
  rowsInCycle,
  statusLabel,
} from './admissionResults.helpers';

export { availableYears, cycleYearOf, rowsInCycle };
export type { CycleYear };

export type Market = 'US' | 'UK' | 'EU' | 'Canada' | 'Asia' | 'Africa' | 'Oceania' | 'Other';
export type Continent = 'North America' | 'Europe' | 'Asia' | 'Africa' | 'Oceania' | 'Other';

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const EU_SET = new Set(EU_COUNTRIES.map(normalize));

export const marketOf = (row: Pick<AdmissionResultRowDTO, 'country' | 'region'>): Market => {
  const country = normalize(row.country ?? '');
  if (country === 'united states' || country === 'usa' || country === 'united states of america')
    return 'US';
  if (country === 'united kingdom' || country === 'uk' || country === 'england') return 'UK';
  if (country === 'canada') return 'Canada';
  if (EU_SET.has(country)) return 'EU';
  const region = normalize(row.region ?? '');
  if (region === 'asia') return 'Asia';
  if (region === 'africa') return 'Africa';
  if (region === 'oceania') return 'Oceania';
  if (region === 'europe') return 'EU';
  return 'Other';
};

export const continentOf = (row: Pick<AdmissionResultRowDTO, 'country' | 'region'>): Continent => {
  const market = marketOf(row);
  if (market === 'US' || market === 'Canada') return 'North America';
  if (market === 'UK' || market === 'EU') return 'Europe';
  if (market === 'Asia' || market === 'Africa' || market === 'Oceania') return market;
  const region = normalize(row.region ?? '');
  if (region === 'americas') return 'North America';
  if (region === 'europe') return 'Europe';
  return 'Other';
};

export const isTop100 = (row: AdmissionResultRowDTO) =>
  typeof row.universityRank === 'number' && row.universityRank <= TOP_100_RANK_LIMIT;
const hasTier = (row: AdmissionResultRowDTO, tier: AdmissionTier) =>
  (row.universityTiers ?? []).includes(tier);
export const isIvy = (row: AdmissionResultRowDTO) => hasTier(row, 'ivy');
export const isOxbridge = (row: AdmissionResultRowDTO) => hasTier(row, 'oxbridge');
export const isTierOneUk = (row: AdmissionResultRowDTO) => hasTier(row, 'tier1_uk');
export const isTierOneEu = (row: AdmissionResultRowDTO) => hasTier(row, 'tier1_eu');
const countryIs = (row: AdmissionResultRowDTO, country: string) =>
  normalize(row.country ?? '') === normalize(country);
export const isNetherlands = (row: AdmissionResultRowDTO) => countryIs(row, NETHERLANDS_COUNTRY);

export const isApplication = (row: AdmissionResultRowDTO) =>
  row.status === GoalStatusTypes.ApplicationSent ||
  row.status === GoalStatusTypes.ApplicationRejected ||
  row.status === GoalStatusTypes.ApplicationWithdrawn ||
  isOffer(row);
export const isRejection = (row: AdmissionResultRowDTO) =>
  row.status === GoalStatusTypes.ApplicationRejected;
export const isDecided = (row: AdmissionResultRowDTO) => isOffer(row) || isRejection(row);
export const isPending = (row: AdmissionResultRowDTO) =>
  row.status === GoalStatusTypes.ApplicationSent;

const unique = (rows: AdmissionResultRowDTO[], pick: (row: AdmissionResultRowDTO) => string) =>
  new Set(rows.map(pick)).size;

const pct = (part: number, total: number) =>
  total ? Math.round((part / total) * 1000) / 10 : null;
const avg = (part: number, total: number) =>
  total ? Math.round((part / total) * 100) / 100 : null;

export type StatLine = { label: string; value: number | string | null; hint?: string };

const fmtPct = (value: number | null) => (value === null ? '—' : `${value}%`);
const fmtNum = (value: number | null) => (value === null ? '—' : String(value));

const estimateOffers = (rows: AdmissionResultRowDTO[]) => {
  const offers = rows.filter(isOffer).length;
  const decided = offers + rows.filter(isRejection).length;
  const pending = rows.filter(isPending).length;
  if (!pending) return offers;
  if (!decided) return null;
  return offers + Math.round(pending * (offers / decided));
};

const ESTIMATE_HINT = 'Recorded offers + pending applications × admission rate';

const admissionRateLine = (label: string, rows: AdmissionResultRowDTO[]): StatLine => {
  const decided = rows.filter(isDecided);
  const offers = decided.filter(isOffer).length;
  return {
    label,
    value: fmtPct(pct(offers, decided.length)),
    hint: `${offers} offers of ${decided.length} decided applications`,
  };
};

export const computeYearStats = (rows: AdmissionResultRowDTO[], year: CycleYear) => {
  const inYear = rowsInCycle(rows, year);
  const applications = inYear.filter(isApplication);
  const offers = inYear.filter(isOffer);
  const studentsApplied = unique(applications, (row) => row.studentId);
  const studentsWithOffers = unique(offers, (row) => row.studentId);
  const avgOffers = avg(offers.length, studentsWithOffers);
  const top100 = offers.filter(isTop100).length;
  const yearLabel = year === 'all' ? 'all years' : String(year);

  const market = (name: Market) => {
    const marketOffers = offers.filter((row) => marketOf(row) === name);
    const marketApplications = applications.filter((row) => marketOf(row) === name);
    const estimated = estimateOffers(marketApplications);
    const studentsWith = unique(marketOffers, (row) => row.studentId);
    const studentsAppliedHere = unique(marketApplications, (row) => row.studentId);
    const average = avg(marketOffers.length, studentsWith);
    return {
      offers: marketOffers,
      applications: marketApplications,
      total: marketOffers.length,
      top100: marketOffers.filter(isTop100).length,
      studentsWith,
      studentsApplied: studentsAppliedHere,
      average,
      estimated,
    };
  };

  const us = market('US');
  const uk = market('UK');
  const eu = market('EU');
  const canada = market('Canada');
  const asia = market('Asia');
  const africa = market('Africa');

  const ivy = us.offers.filter(isIvy).length;
  const oxbridge = uk.offers.filter(isOxbridge).length;
  const tier1Uk = uk.offers.filter(isTierOneUk).length;
  const tier1Eu = eu.offers.filter(isTierOneEu).length;
  const nlExclTier1 = eu.offers.filter((row) => isNetherlands(row) && !isTierOneEu(row)).length;

  const overall: StatLine[] = [
    { label: `Total offers ${yearLabel}`, value: offers.length },
    {
      label: 'Total Top 100 offers',
      value: top100,
      hint: 'QS overall rank ≤ 100 in the edition of the admission cycle',
    },
    { label: '% Top 100 offers', value: fmtPct(pct(top100, offers.length)) },
    { label: 'Students with recorded offers in Agora', value: studentsWithOffers },
    {
      label: 'Students who applied with us',
      value: studentsApplied,
      hint: 'At least one application sent, decided or withdrawn',
    },
    { label: 'Avg number of offers / student', value: fmtNum(avgOffers) },
    {
      label: 'Estimated total number of offers',
      value: estimateOffers(applications) ?? '—',
      hint: ESTIMATE_HINT,
    },
  ];

  const usLines: StatLine[] = [
    { label: 'Total US offers', value: us.total },
    { label: 'Total Top 100 US offers', value: us.top100 },
    { label: '% Top 100 of US offers', value: fmtPct(pct(us.top100, us.total)) },
    { label: 'Ivy offers', value: ivy },
    { label: 'Ivy offers / total US offers', value: fmtPct(pct(ivy, us.total)) },
    { label: 'US students with recorded offers', value: us.studentsWith },
    { label: 'Avg number of US offers / student', value: fmtNum(us.average) },
    {
      label: 'US UPG success rate',
      value: fmtPct(pct(us.studentsWith, us.studentsApplied)),
      hint: 'Students with a US offer ÷ students who applied to the US',
    },
  ];

  const ukLines: StatLine[] = [
    { label: 'Total UK offers', value: uk.total },
    { label: 'Total Top 100 UK offers', value: uk.top100 },
    { label: '% Top 100 of UK offers', value: fmtPct(pct(uk.top100, uk.total)) },
    { label: 'Oxbridge offers', value: oxbridge },
    {
      label: 'Estimated total number of UK offers',
      value: uk.estimated ?? '—',
      hint: ESTIMATE_HINT,
    },
    { label: 'Tier I UK offers', value: tier1Uk },
    { label: 'Tier I UK offers / total UK offers', value: fmtPct(pct(tier1Uk, uk.total)) },
    { label: 'UK students with recorded offers', value: uk.studentsWith },
    { label: 'Avg number of UK offers / student', value: fmtNum(uk.average) },
    {
      label: 'UK UPG success rate',
      value: fmtPct(pct(uk.studentsWith, uk.studentsApplied)),
      hint: 'Students with a UK offer ÷ students who applied to the UK',
    },
  ];

  const euLines: StatLine[] = [
    { label: 'Total EU offers', value: eu.total },
    { label: 'Total Top 100 EU offers', value: eu.top100 },
    { label: '% Top 100 of EU offers', value: fmtPct(pct(eu.top100, eu.total)) },
    { label: 'Tier I EU offers', value: tier1Eu },
    { label: 'Tier I EU offers / total EU offers', value: fmtPct(pct(tier1Eu, eu.total)) },
    { label: 'NL offers (excluding Tier I)', value: nlExclTier1 },
    { label: 'NL offers / total EU offers', value: fmtPct(pct(nlExclTier1, eu.total)) },
    { label: 'EU students with recorded offers', value: eu.studentsWith },
    { label: 'Avg number of EU offers / student', value: fmtNum(eu.average) },
    {
      label: 'Estimated total number of EU offers',
      value: eu.estimated ?? '—',
      hint: ESTIMATE_HINT,
    },
    {
      label: 'EU UPG success rate',
      value: fmtPct(pct(eu.studentsWith, eu.studentsApplied)),
      hint: 'Students with an EU offer ÷ students who applied to the EU',
    },
  ];

  const otherLines: StatLine[] = [
    { label: 'Africa offers', value: africa.total },
    { label: 'Asia offers', value: asia.total },
    { label: 'Canada offers', value: canada.total },
    { label: 'Total Top 100 Canada offers', value: canada.top100 },
  ];

  const admissionRates: StatLine[] = [
    admissionRateLine('UK Admission Rate', uk.applications),
    admissionRateLine('Oxbridge Admission Rate', uk.applications.filter(isOxbridge)),
    admissionRateLine('US Admission Rate', us.applications),
    admissionRateLine('Ivy League Admission Rate', us.applications.filter(isIvy)),
    admissionRateLine(
      'Italy Admission Rate',
      applications.filter((row) => countryIs(row, 'Italy'))
    ),
    admissionRateLine('Netherlands Admission Rate', applications.filter(isNetherlands)),
    admissionRateLine(
      'Spain Admission Rate',
      applications.filter((row) => countryIs(row, 'Spain'))
    ),
    admissionRateLine(
      'France Admission Rate',
      applications.filter((row) => countryIs(row, 'France'))
    ),
    admissionRateLine('EU Admission Rate', eu.applications),
  ];

  const rankEditions = [
    ...new Set(
      inYear.map((row) => row.universityRankYear).filter((year): year is number => !!year)
    ),
  ].sort((a, b) => b - a);

  return {
    admissionRates,
    rankEditions,
    overall,
    us: usLines,
    uk: ukLines,
    eu: euLines,
    other: otherLines,
    offersCount: offers.length,
  };
};

export type GroupStats = {
  key: string;
  label: string;
  offers: number;
  rejections: number;
  applications: number;
  students: number;
  studentsWithOffers: number;
  acceptanceRate: number | null;
  top100: number;
  topUniversities: string[];
};

const groupStats = (
  rows: AdmissionResultRowDTO[],
  keysOf: (row: AdmissionResultRowDTO) => string[]
): GroupStats[] => {
  const groups = new Map<string, AdmissionResultRowDTO[]>();
  rows.forEach((row) =>
    keysOf(row).forEach((key) => groups.set(key, [...(groups.get(key) ?? []), row]))
  );
  return [...groups.entries()]
    .map(([key, groupRows]) => {
      const offers = groupRows.filter(isOffer);
      const rejections = groupRows.filter(isRejection);
      const universities = new Map<string, number>();
      offers.forEach((row) =>
        universities.set(row.universityName, (universities.get(row.universityName) ?? 0) + 1)
      );
      return {
        key,
        label: key,
        offers: offers.length,
        rejections: rejections.length,
        applications: groupRows.filter(isApplication).length,
        students: unique(groupRows, (row) => row.studentId),
        studentsWithOffers: unique(offers, (row) => row.studentId),
        acceptanceRate: pct(offers.length, offers.length + rejections.length),
        top100: offers.filter(isTop100).length,
        topUniversities: [...universities.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([name, count]) => `${name} (${count})`),
      };
    })
    .sort((a, b) => b.offers - a.offers || a.label.localeCompare(b.label));
};

export const statsByMajor = (rows: AdmissionResultRowDTO[]) =>
  groupStats(rows, (row) => (row.majors.length ? row.majors : ['No major recorded']));

export const statsByContinent = (rows: AdmissionResultRowDTO[]) =>
  groupStats(rows, (row) => [continentOf(row)]);

export type MentorStudentResult = {
  studentId: string;
  studentName: string;
  graduationYear?: number;
  sessions: number;
  results: { university: string; country: string; status: number; top100: boolean }[];
};

export type MentorStats = {
  id: string;
  name: string;
  courses: string[];
  students: number;
  studentsWithOffers: number;
  offers: number;
  rejections: number;
  applications: number;
  acceptanceRate: number | null;
  top100: number;
  ivy: number;
  oxbridge: number;
  tier1: number;
  studentResults: MentorStudentResult[];
};

export const statsByMentor = (rows: AdmissionResultRowDTO[]): MentorStats[] => {
  const groups = new Map<
    string,
    {
      name: string;
      courses: Set<string>;
      rows: AdmissionResultRowDTO[];
      sessions: Map<string, number>;
    }
  >();
  rows.forEach((row) =>
    row.mentors.forEach((mentor) => {
      const group = groups.get(mentor.id) ?? {
        name: mentor.name,
        courses: new Set<string>(),
        rows: [],
        sessions: new Map<string, number>(),
      };
      group.rows.push(row);
      mentor.courses.forEach((course) => group.courses.add(course));
      group.sessions.set(
        row.studentId,
        Math.max(group.sessions.get(row.studentId) ?? 0, mentor.sessions)
      );
      groups.set(mentor.id, group);
    })
  );

  return [...groups.entries()]
    .map(([id, group]) => {
      const offers = group.rows.filter(isOffer);
      const rejections = group.rows.filter(isRejection);
      const byStudent = new Map<string, MentorStudentResult>();
      group.rows.forEach((row) => {
        const entry = byStudent.get(row.studentId) ?? {
          studentId: row.studentId,
          studentName: row.studentName,
          graduationYear: row.graduationYear,
          sessions: group.sessions.get(row.studentId) ?? 0,
          results: [],
        };
        entry.results.push({
          university: row.universityName,
          country: row.country,
          status: row.status,
          top100: isTop100(row),
        });
        byStudent.set(row.studentId, entry);
      });
      return {
        id,
        name: group.name,
        courses: [...group.courses],
        students: byStudent.size,
        studentsWithOffers: unique(offers, (row) => row.studentId),
        offers: offers.length,
        rejections: rejections.length,
        applications: group.rows.filter(isApplication).length,
        acceptanceRate: pct(offers.length, offers.length + rejections.length),
        top100: offers.filter(isTop100).length,
        ivy: offers.filter(isIvy).length,
        oxbridge: offers.filter(isOxbridge).length,
        tier1: offers.filter((row) => isTierOneUk(row) || isTierOneEu(row)).length,
        studentResults: [...byStudent.values()].sort((a, b) =>
          a.studentName.localeCompare(b.studentName)
        ),
      };
    })
    .sort((a, b) => b.offers - a.offers || a.name.localeCompare(b.name));
};

export const downloadCsv = (fileName: string, csv: string) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const statLinesCsv = (sections: { title: string; lines: StatLine[] }[]) =>
  stringify([
    ['Section', 'Metric', 'Value'],
    ...sections.flatMap((section) =>
      section.lines.map((line) => [section.title, line.label, line.value ?? ''])
    ),
  ]);

export const groupStatsCsv = (label: string, groups: GroupStats[]) =>
  stringify([
    [
      label,
      'Offers',
      'Rejections',
      'Applications',
      'Students',
      'Students with offers',
      'Acceptance rate %',
      'Top 100 offers',
      'Top universities',
    ],
    ...groups.map((group) => [
      group.label,
      group.offers,
      group.rejections,
      group.applications,
      group.students,
      group.studentsWithOffers,
      group.acceptanceRate ?? '',
      group.top100,
      group.topUniversities.join('; '),
    ]),
  ]);

export const mentorStatsCsv = (mentors: MentorStats[]) =>
  stringify([
    [
      'Mentor',
      'Courses',
      'Students',
      'Students with offers',
      'Offers',
      'Rejections',
      'Applications',
      'Acceptance rate %',
      'Top 100 offers',
      'Ivy offers',
      'Oxbridge offers',
      'Tier I offers',
      'Student',
      'Graduation year',
      'Sessions before result',
      'University',
      'Country',
      'Result',
    ],
    ...mentors.flatMap((mentor) =>
      mentor.studentResults.flatMap((student) =>
        student.results.map((result) => [
          mentor.name,
          mentor.courses.join('; '),
          mentor.students,
          mentor.studentsWithOffers,
          mentor.offers,
          mentor.rejections,
          mentor.applications,
          mentor.acceptanceRate ?? '',
          mentor.top100,
          mentor.ivy,
          mentor.oxbridge,
          mentor.tier1,
          student.studentName,
          student.graduationYear ?? '',
          student.sessions,
          result.university,
          result.country,
          statusLabel(result.status),
        ])
      )
    ),
  ]);
